import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTA from "@/components/CTA";
import Seo from "@/components/Seo";
import { MapPin, CaretRight } from "@phosphor-icons/react";

export function ServiceAreasIndex() {
  const [states, setStates] = useState([]);
  useEffect(() => { api.get("/states").then((r) => setStates(r.data)); }, []);
  return (
    <div data-testid="service-areas-index">
      <Seo title="Service Areas — Nevada & Utah Surveillance Coverage" description="360 View LLC serves approved locations across Nevada and expanding Utah markets with rapid-deployment solar surveillance systems." path="/service-areas" />
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "Service Areas" }]} /></section>
      <section className="container-x pb-10">
        <div className="eyebrow text-solar mb-3">Service Areas</div>
        <h1 className="mb-4">Nevada and Utah coverage.</h1>
        <p className="text-white/75 text-lg max-w-2xl">
          Nevada active. Utah expanding — contact us to confirm availability for your city and project.
        </p>
      </section>
      <section className="container-x pb-20 grid md:grid-cols-2 gap-px bg-chrome border border-chrome">
        {states.map((s) => (
          <Link key={s.slug} to={`/service-areas/${s.slug}`} data-testid={`state-card-${s.slug}`} className="bg-obsidian p-8 hover:bg-steel transition-colors group">
            <MapPin size={32} weight="duotone" className="text-solar mb-4" />
            <div className="font-display uppercase text-3xl font-bold mb-2 group-hover:text-solar transition-colors">{s.title}</div>
            <div className="eyebrow text-solar mb-3">{s.status_label}</div>
            <p className="text-white/70 leading-relaxed">{s.summary}</p>
          </Link>
        ))}
      </section>
      <CTA />
    </div>
  );
}

export function StateDetail() {
  const { state } = useParams();
  const [st, setSt] = useState(null);
  const [cities, setCities] = useState([]);
  useEffect(() => {
    api.get(`/states/${state}`).then((r) => setSt(r.data)).catch(() => setSt(false));
    api.get(`/cities`, { params: { state_slug: state } }).then((r) => setCities(r.data));
  }, [state]);
  if (st === false) return <div className="container-x py-20"><h1>State not found</h1></div>;
  if (!st) return <div className="container-x py-20 text-white/60">Loading…</div>;
  return (
    <div data-testid={`state-detail-${state}`}>
      <Seo
        title={`Solar Surveillance ${st.title}`}
        description={st.summary}
        path={`/service-areas/${state}`}
        breadcrumb={[{ label: "Home", path: "/" }, { label: "Service Areas", path: "/service-areas" }, { label: st.title }]}
      />
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "Service Areas", to: "/service-areas" }, { label: st.title }]} /></section>
      <section className="container-x pb-12">
        <div className="eyebrow text-solar mb-3">{st.status_label}</div>
        <h1 className="mb-5">{st.title}</h1>
        <p className="text-white/80 text-lg max-w-2xl leading-relaxed mb-6">{st.summary}</p>
        <Link to="/request-a-quote" className="btn-solar inline-block">Confirm Availability for Your Site</Link>
      </section>
      {cities.length > 0 && (
        <section className="container-x pb-20">
          <div className="eyebrow text-solar mb-3">Cities</div>
          <h2 className="mb-8">Active and expansion cities.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-chrome border border-chrome">
            {cities.map((c) => (
              <Link key={c.slug} to={`/service-areas/${state}/${c.slug}`} className="bg-obsidian p-6 hover:bg-steel transition-colors group">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display uppercase text-xl font-bold group-hover:text-solar transition-colors">{c.title}</div>
                  <CaretRight size={16} weight="bold" className="text-solar" />
                </div>
                <div className="eyebrow text-solar text-[10px]">{c.status_label}</div>
              </Link>
            ))}
          </div>
        </section>
      )}
      <CTA />
    </div>
  );
}

export function CityDetail() {
  const { state, city } = useParams();
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/cities/${city}`).then((r) => setC(r.data)).catch(() => setC(false)); }, [city]);
  if (c === false) return <div className="container-x py-20"><h1>City not found or not yet published.</h1></div>;
  if (!c) return <div className="container-x py-20 text-white/60">Loading…</div>;
  const isExpansion = c.service_status === "expansion";
  return (
    <div data-testid={`city-detail-${city}`}>
      <Seo
        title={`Solar Surveillance in ${c.title}, ${c.state_name}`}
        description={c.summary}
        path={`/service-areas/${state}/${city}`}
        breadcrumb={[{ label: "Home", path: "/" }, { label: "Service Areas", path: "/service-areas" }, { label: c.state_name, path: `/service-areas/${state}` }, { label: c.title }]}
      />
      <section className="container-x pt-10 pb-6">
        <Breadcrumbs items={[{ label: "Service Areas", to: "/service-areas" }, { label: c.state_name, to: `/service-areas/${state}` }, { label: c.title }]} />
      </section>
      <section className="container-x pb-14">
        <div className="eyebrow text-solar mb-3">{c.status_label}</div>
        <h1 className="mb-5">Solar Surveillance in {c.title}, {c.state_name}</h1>
        <p className="text-white/80 text-lg max-w-3xl leading-relaxed mb-8">{c.summary}</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/request-a-quote" className="btn-solar inline-block text-center">{isExpansion ? "Confirm Availability" : "Request Assessment"}</Link>
          <a href="tel:7029290390" className="btn-ghost inline-block text-center">Call 702-929-0390</a>
        </div>
      </section>
      <section className="bg-steel border-y border-chrome py-14">
        <div className="container-x grid lg:grid-cols-2 gap-10">
          <div>
            <div className="eyebrow text-solar mb-3">Common Local Use Cases</div>
            <ul className="space-y-2 text-white/85">
              {["Active construction sites", "Commercial property perimeters", "Auto dealership lots", "Equipment yards", "Parking lot surveillance", "Vacant commercial property"].map((u) => (
                <li key={u} className="flex items-start gap-2"><span className="w-1.5 h-1.5 bg-solar mt-2" />{u}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="eyebrow text-solar mb-3">Service Notes</div>
            <p className="text-white/80 leading-relaxed">
              Deployment timing depends on unit availability, travel to your site and site access.
              {isExpansion && " Utah service coverage is expanding — please contact us to confirm operational capacity for your project."}
            </p>
          </div>
        </div>
      </section>
      <CTA />
    </div>
  );
}
